// Helpers for the inline (autosave) unit table editor. Kept free of React so
// the normalisation and validation rules can be unit-tested on their own.

export const INLINE_TEXT_FIELDS = ['name', 'uprn', 'plotNumber', 'area']
export const INLINE_EDITABLE_FIELDS = [...INLINE_TEXT_FIELDS, 'floorNumbers']

const AREA_PATTERN = /^[0-9]*(\.[0-9]+)?$/

// Floors are stored as strings on the API ('-1', 'lower_ground', '0', '3') but
// older records and some UI helpers use numbers (0, -0.5). Normalise so values
// can be compared and matched against select options with ===.
export const normalizeFloorValue = value => {
  if (value === 'lower_ground' || value === 'LG' || value === -0.5) return 'lower_ground'
  if (value === 'ground') return '0'
  if (value == null || value === '') return null
  const n = Number(value)
  if (Number.isFinite(n)) return String(n)
  return String(value)
}

export const normalizeFloorValues = floors =>
  Array.from(
    new Set(
      (Array.isArray(floors) ? floors : [])
        .map(normalizeFloorValue)
        .filter(value => value != null)
    )
  )

export const getInlineFieldValue = (unit, field) => {
  if (field === 'floorNumbers') {
    return normalizeFloorValues(unit?.floorNumbers ?? unit?.floor_numbers)
  }
  if (field === 'plotNumber') {
    return String(unit?.plotNumber ?? unit?.plot_number ?? '')
  }
  return String(unit?.[field] ?? '')
}

const sameFloors = (a, b) => {
  const left = [...normalizeFloorValues(a)].sort()
  const right = [...normalizeFloorValues(b)].sort()
  return left.length === right.length && left.every((v, i) => v === right[i])
}

// Turns a raw cell value into the PATCH body for that field.
// Returns { changed: false } when the value equals what the unit already has,
// { error } when the value is invalid, or { changed: true, patch } otherwise.
export const buildInlineUnitPatch = (field, rawValue, unit) => {
  if (field === 'floorNumbers') {
    const floors = normalizeFloorValues(rawValue)
    if (sameFloors(floors, getInlineFieldValue(unit, 'floorNumbers'))) {
      return { changed: false }
    }
    return { changed: true, patch: { floorNumbers: floors } }
  }

  const value = String(rawValue ?? '').trim()
  const current = getInlineFieldValue(unit, field).trim()

  if (field === 'name') {
    if (!value) return { error: 'Unit name is required' }
    if (value === current) return { changed: false }
    return { changed: true, patch: { name: value } }
  }

  if (field === 'area') {
    if (value && !AREA_PATTERN.test(value)) return { error: 'Incorrect area' }
    if (value && !(Number(value) > 0)) return { error: 'Area must be a positive number' }
    if (value === current) return { changed: false }
    return { changed: true, patch: { area: value ? Number(value) : null } }
  }

  if (field === 'uprn' || field === 'plotNumber') {
    if (value === current) return { changed: false }
    return { changed: true, patch: { [field]: value || null } }
  }

  return { error: `Unknown field: ${field}` }
}
